import React from 'react';

import PropTypes from 'prop-types';
import DataSheet from 'react-datasheet';

import TableControls from './TableControls';
import TableDataEditor from './TableDataEditor';

import 'react-datasheet/lib/react-datasheet.css';

const gridRow = PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    style: PropTypes.object,
}));

export default class EditableTable extends React.PureComponent {
    static propTypes = {
        grid: PropTypes.arrayOf(gridRow),
        onChange: PropTypes.func.isRequired,
    };

    state = {
        grid: this.props.grid,
        prevStates: [],
        nextStates: [],
    };

    table = React.createRef();

    tableControls = React.createRef();

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keydown', this.handleKeydown);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleKeydown);
    }

    valueRenderer(cell) {
        return cell.value;
    }

    handleKeydown = (e) => {
        if ((e.key.toLowerCase() === 'z' && e.metaKey && !e.shiftKey) || (e.key.toLowerCase() === 'z' && e.ctrlKey && !e.shiftKey)) {
            return this.undoGrid();
        }

        if ((e.key.toLowerCase() === 'z' && e.metaKey && e.shiftKey) || (e.key.toLowerCase() === 'z' && e.ctrlKey && e.shiftKey)) {
            return this.redoGrid();
        }

        if ((e.key === 'b' && e.metaKey) || (e.key === 'b' && e.ctrlKey)) {
            return this.toggleBold();
        }

        if ((e.key === 'i' && e.metaKey) || (e.key === 'i' && e.ctrlKey)) {
            return this.toggleItalic();
        }
    };

    handleCellChange = (changes) => {
        const { grid } = this.state;

        const newGrid = grid.map((row) => [...row]);

        changes.forEach(({
            row, col, value,
        }) => {
            newGrid[row][col] = { ...newGrid[row][col], value };
        });

        this.updateGrid(newGrid);
    };

    handleClickOutside = (e) => {
        if (this.table && !this.table.current.dgDom.contains(e.target) // клик вне таблицы
        && this.tableControls && !this.tableControls.current.contains(e.target) // клик вне контролов таблицы
        && !e.target.classList.contains('colorpicker__color')) { // клик вне колорпикера
            this.removeSelect();
        }
    };

    handleChange = () => {
        const { onChange } = this.props;
        const { grid } = this.state;
        const { dgDom } = this.table.current;

        const tableNode = dgDom.cloneNode(true);
        tableNode.removeAttribute('tabindex');

        onChange(grid, tableNode.outerHTML);
    };

    handleAttrRender = (cell) => ({
        style: cell.style || null,
        colSpan: cell.colSpan || null,
    });

    setSelect = (cells) => {
        this.setState({
            selected: cells,
        });
    };

    removeSelect = () => {
        this.setState({
            selected: null,
        });
    };

    setColSpan = (row, { jStart, jEnd }) => {
        const newGridRow = JSON.parse(JSON.stringify(row));

        const cellsSelected = jEnd - jStart + 1;
        const prevColspan = newGridRow[jStart].colSpan;

        newGridRow[jStart].colSpan = cellsSelected > row.length - jStart ? row.length - jStart : cellsSelected;

        if (!prevColspan || cellsSelected > 1) {
            for (let i = jStart + 1; i < jEnd + 1; i++) {
                if (!newGridRow[i]) continue; // если выделены ячейки за границей таблицы

                newGridRow[i].style = { display: 'none' };
            }
        } else {
            for (let i = jStart + 1; i < jStart + prevColspan; i++) {
                if (!newGridRow[i]) continue; // если выделены ячейки за границей таблицы

                newGridRow[jStart].colSpan = null;
                newGridRow[i].style = {};
            }
        }

        return newGridRow;
    };

    setStyle = (cellStyles, newStyle) => {
        const styleName = Object.keys(newStyle)[0];
        const isTogglable = styleName === 'fontWeight' || styleName === 'fontStyle';

        // Если у ячейки уже есть добавляемый стиль, возвращаем null
        if (isTogglable && cellStyles && cellStyles.hasOwnProperty(styleName) && cellStyles[styleName] === newStyle[styleName]) {
            return {
                ...cellStyles,
                [styleName]: null,
            };
        }

        return {
            ...cellStyles,
            ...newStyle,
        };
    };

    setAlignment = (side) => {
        this.changeSelectedCells({ textAlign: side });
    };

    setColor = (color) => {
        this.changeSelectedCells(color);
    };

    toggleBold = () => {
        this.changeSelectedCells({ fontWeight: 'bold' });
    };

    toggleItalic = () => {
        this.changeSelectedCells({ fontStyle: 'italic' });
    };

    toggleColspan = () => {
        this.changeSelectedCells(2, 'colSpan');
    };

    changeSelectedCells = (value, key = 'style') => {
        const { grid, selected } = this.state;

        if (!selected) {
            return false;
        }

        // клонируем сетку для иммутабельности
        const newGrid = JSON.parse(JSON.stringify(grid));

        // проверяем реверснуто ли выделение (выделяли справа налево и/или снизу вверх)
        const isRowsReversed = selected.start.i > selected.end.i;
        const isCellsReversed = selected.start.j > selected.end.j;

        // если выделение реверснуто, разворачиваем его в правильную сторону
        const iStart = isRowsReversed ? selected.end.i : selected.start.i;
        const iEnd = isRowsReversed ? selected.start.i : selected.end.i;

        const jStart = isCellsReversed ? selected.end.j : selected.start.j;
        const jEnd = isCellsReversed ? selected.start.j : selected.end.j;

        // применяем форматирование к ячейкам в выделенном куске таблицы
        for (let i = iStart; i < iEnd + 1; i++) {
            if (!newGrid[i]) continue; // если выделены строки за границей таблицы

            for (let j = jStart; j < jEnd + 1; j++) {
                if (!newGrid[i][j]) continue; // если выделены ячейки за границей таблицы

                if (key === 'style') {
                    newGrid[i][j][key] = this.setStyle(newGrid[i][j][key], value);
                }

                if (key === 'colSpan') {
                    if (iEnd - iStart > 0) return;

                    newGrid[i] = this.setColSpan(newGrid[i], { jStart, jEnd });
                    break;
                }
            }
        }

        this.updateGrid(newGrid);
    };

    updateGrid = (newGrid) => {
        const { grid, prevStates } = this.state;

        this.setState({
            grid: newGrid,
            prevStates: [
                ...prevStates,
                grid,
            ],
            nextStates: [],
        }, this.handleChange);
    };

    undoGrid = () => {
        const { grid, prevStates, nextStates } = this.state;

        if (!prevStates.length) {
            return false;
        }

        const slicedPrevStates = prevStates.slice(0, -1);
        const prevGridState = prevStates[prevStates.length - 1];

        this.setState({
            grid: prevGridState,
            prevStates: slicedPrevStates,
            nextStates: [
                ...nextStates,
                grid,
            ],
        }, this.handleChange);
    };

    redoGrid = () => {
        const { grid, prevStates, nextStates } = this.state;

        if (!nextStates.length) {
            return false;
        }

        const slicedNextStates = nextStates.slice(0, -1);
        const nextGridState = nextStates[nextStates.length - 1];

        this.setState({
            grid: nextGridState,
            nextStates: slicedNextStates,
            prevStates: [
                ...prevStates,
                grid,
            ],
        }, this.handleChange);
    };

    addColumn = () => {
        const { grid } = this.state;

        this.removeSelect();

        const newGrid = [];

        grid.map((row, i) => {
            // копируем в новую колонку стили ячеек соседней колонки
            const { style = {} } = row[row.length - 1];
            const { display: _, ...otherStyles } = style; // кроме display

            return newGrid[i] = [
                ...row,
                {
                    value: '',
                    style: otherStyles,
                },
            ];
        });

        this.updateGrid(newGrid);
    };

    removeColumn = () => {
        const { grid } = this.state;

        this.removeSelect();

        if (grid[0].length <= 1) {
            return false;
        }

        const newGrid = [];
        grid.map((row, i) => newGrid[i] = grid[i].slice(0, -1));

        this.updateGrid(newGrid);
    };

    addRow = () => {
        const { grid } = this.state;

        this.removeSelect();

        const newRow = []; // копируем в новую строку стили ячеек последней строки
        grid[grid.length - 1].map((cell, i) => {
            const { style = {} } = cell;
            const { display: _, ...otherStyles } = style;

            return newRow[i] = {
                value: '',
                style: otherStyles,
            };
        });

        const newGrid = [
            ...grid,
            newRow,
        ];

        this.updateGrid(newGrid);
    };

    removeRow = () => {
        const { grid } = this.state;

        this.removeSelect();

        if (grid.length <= 1) {
            return false;
        }

        const newGrid = grid.slice(0, -1);

        this.updateGrid(newGrid);
    };

    getSpanDisabilityStatus = () => {
        const { selected, grid } = this.state;

        if (!selected) return true; // нет выделения

        const { start, end } = selected;

        if (start.i !== end.i) return true; // выделены ячейки на разных строках

        if (start.j === end.j && !grid[start.i][start.j].colSpan) return true; // выделена одна ячейка, и у нее нет атрибута colSpan

        return false;
    };

    render() {
        const {
            grid, selected, prevStates, nextStates,
        } = this.state;

        return (
            <div>
                <TableControls
                    innerRef={this.tableControls}
                    addColumn={this.addColumn}
                    addRow={this.addRow}
                    removeColumn={this.removeColumn}
                    removeRow={this.removeRow}
                    toggleBold={this.toggleBold}
                    toggleItalic={this.toggleItalic}
                    toggleColspan={this.toggleColspan}
                    setAlignment={this.setAlignment}
                    setColor={this.setColor}
                    undo={this.undoGrid}
                    redo={this.redoGrid}
                    undoable={!!prevStates.length}
                    redoable={!!nextStates.length}
                    unspanable={this.getSpanDisabilityStatus()}
                />
                <DataSheet
                    data={grid}
                    valueRenderer={this.valueRenderer}
                    onCellsChanged={this.handleCellChange}
                    dataEditor={TableDataEditor}
                    ref={this.table}
                    className="table"
                    onSelect={this.setSelect}
                    selected={selected}
                    attributesRenderer={this.handleAttrRender}
                />
                {/* <a className="table__about" title="Справка" target="_blank" href="https://docs.google.com/document/d/1ZrW-ax9edOEMgQK_llv4qdn4fj_-QNIBq3fZc2iGyL8/edit?usp=sharing">
                    <FontAwesomeIcon icon="question-circle" />
                </a> */}
            </div>
        );
    }
}

EditableTable.defaultProps = {
    grid: [
        [{ value: '' }, { value: '' }],
        [{ value: '' }, { value: '' }],
    ],
};
